/**
 * Chat Fragment — captcha gate + streaming-POST handler.
 *
 * HTMX-first design. The server renders all bubble HTML in Jinja
 * templates and ships rendered fragments inside SSE frames. The JS
 * here only does what HTMX can't: parse an SSE stream that arrives on
 * the response body of a POST request (EventSource is GET-only).
 *
 * Each SSE frame's `data` is a complete HTML fragment. The JS drops
 * `user_message`, `streaming_bubble`, and `error` frames into
 * #chat-messages with insertAdjacentHTML. `chunk` and `done` frames
 * replace the in-flight streaming bubble via outerHTML (the rendered
 * agent bubble carries id="current-agent-bubble" for chunk frames so
 * the swap target is unambiguous).
 *
 * One special case: the user-side image bubble. The server doesn't
 * have the uploaded image bytes back, so chat_fragment.js builds that
 * one bubble locally from a URL.createObjectURL(file) blob URL.
 *
 * Session persistence: the DOM is the chat's only state (thread id and
 * metadata live in hidden form fields, history lives in the bubbles),
 * so persistence is a sessionStorage snapshot of the panel mount's
 * innerHTML, saved at turn boundaries and restored when the launcher
 * is clicked — the panel GET is skipped and the snapshot injected
 * instead, reusing the still-open upstream thread. Snapshots expire
 * after SNAPSHOT_TTL_MS of inactivity and die with the tab. When the
 * upstream reports the thread itself has expired (thread_expired SSE
 * frame), the snapshot is cleared and the user reopens the panel to
 * start fresh.
 */

let recaptchaLoadPromise = null;
let initialized = false;
let streaming = false;

const SNAPSHOT_KEY = 'leapfile.chat.snapshot.v1';
const SNAPSHOT_TTL_MS = 60 * 60 * 1000;
const PANEL_URL = '/app/chat/panel';

function saveSnapshot() {
    const mount = document.getElementById('chat-panel-mount');
    // The null check guards the close-mid-stream case: closeChatPanel()
    // empties the mount while a stream is in flight, and serializing
    // the now-empty mount would wipe the stored conversation.
    const messages = document.querySelector('[data-chat-messages]');
    if (!mount || !messages) return;
    // A thread_expired notice means the upstream thread is dead and the
    // snapshot was already cleared — never re-persist the dead state.
    if (mount.querySelector('[data-chat-thread-expired]')) return;
    // innerHTML serializes attributes, but form values live on DOM
    // properties — sync them or the snapshot keeps the initial values.
    mount.querySelectorAll('input[type="hidden"]').forEach((el) => {
        el.setAttribute('value', el.value);
    });
    const clone = mount.cloneNode(true);
    // blob: URLs die with the document — a restored <img> would render
    // broken. Swap for a text placeholder (captions persist as-is).
    clone.querySelectorAll('img[src^="blob:"]').forEach((img) => {
        const placeholder = document.createElement('div');
        placeholder.className = 'px-4 py-2 text-white/90 text-sm italic';
        placeholder.textContent = 'Image attachment';
        img.replaceWith(placeholder);
    });
    try {
        sessionStorage.setItem(
            SNAPSHOT_KEY,
            JSON.stringify({ t: Date.now(), html: clone.innerHTML }),
        );
    } catch (err) {
        // Quota exceeded or storage disabled — persistence is best-effort.
    }
}

function loadSnapshot() {
    try {
        const raw = sessionStorage.getItem(SNAPSHOT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.html !== 'string' || typeof parsed.t !== 'number'
            || Date.now() - parsed.t > SNAPSHOT_TTL_MS) {
            clearSnapshot();
            return null;
        }
        return parsed.html;
    } catch (err) {
        clearSnapshot();
        return null;
    }
}

function clearSnapshot() {
    try {
        sessionStorage.removeItem(SNAPSHOT_KEY);
    } catch (err) {
        // Storage disabled — nothing to clear.
    }
}

function loadRecaptcha() {
    if (recaptchaLoadPromise) return recaptchaLoadPromise;
    if (window.grecaptcha && window.grecaptcha.render) {
        recaptchaLoadPromise = Promise.resolve();
        return recaptchaLoadPromise;
    }
    recaptchaLoadPromise = new Promise((resolve, reject) => {
        window.__leapfileChatRecaptchaLoad = function () { resolve(); };
        const script = document.createElement('script');
        script.src =
            'https://www.google.com/recaptcha/api.js?onload=__leapfileChatRecaptchaLoad&render=explicit';
        script.async = true;
        script.defer = true;
        script.onerror = () => reject(new Error('Failed to load Google reCAPTCHA'));
        document.head.appendChild(script);
    });
    return recaptchaLoadPromise;
}

function renderCaptchaInto(gateEl) {
    const container = gateEl.querySelector('#recaptcha-container');
    const tokenInput = gateEl.querySelector('[data-chat-captcha-token]');
    const startBtn = gateEl.querySelector('[data-chat-start-btn]');
    const errorEl = gateEl.querySelector('[data-chat-captcha-error]');
    const sitekey = gateEl.dataset.sitekey || '';
    if (!container || !tokenInput || !startBtn) return;
    if (!sitekey) {
        if (errorEl) {
            errorEl.textContent =
                'Chat is unavailable — reCAPTCHA site key is not configured.';
            errorEl.classList.remove('hidden');
        }
        return;
    }
    if (container.dataset.rendered === 'true') return;
    container.dataset.rendered = 'true';
    loadRecaptcha()
        .then(() => {
            window.grecaptcha.render(container, {
                sitekey: sitekey,
                callback: (token) => {
                    tokenInput.value = token || '';
                    startBtn.disabled = !token;
                },
                'expired-callback': () => {
                    tokenInput.value = '';
                    startBtn.disabled = true;
                },
                'error-callback': () => {
                    tokenInput.value = '';
                    startBtn.disabled = true;
                    if (errorEl) {
                        errorEl.textContent = 'Verification error. Please try again.';
                        errorEl.classList.remove('hidden');
                    }
                },
            });
        })
        .catch((err) => {
            if (errorEl) {
                errorEl.textContent =
                    'Could not load verification widget. Please try again.';
                errorEl.classList.remove('hidden');
            }
            console.error('[chat] reCAPTCHA load failed:', err);
        });
}

function activateCaptchaGates(root) {
    const gates = root.querySelectorAll
        ? root.querySelectorAll('[data-chat-captcha-gate]')
        : [];
    gates.forEach((gate) => renderCaptchaInto(gate));
}

function scrollChatToBottom() {
    const messages = document.querySelector('[data-chat-messages]');
    if (messages) messages.scrollTop = messages.scrollHeight;
}

function setStreamingDisabled(disabled) {
    streaming = disabled;
    const panel = document.getElementById('chat-panel');
    if (!panel) return;
    // Don't disable the file input itself — disabled file inputs are
    // excluded from FormData serialization, which would drop the image
    // from the multipart upload. Disabling the paperclip button is
    // enough to prevent re-entry.
    panel.querySelectorAll(
        '[data-chat-message-form] textarea, ' +
        '[data-chat-message-form] button[type="submit"], ' +
        '[data-chat-suggestion], ' +
        '[data-chat-upload-btn], ' +
        '[data-chat-new]'
    ).forEach((el) => { el.disabled = disabled; });
    if (!disabled) {
        const textarea = panel.querySelector('[data-chat-message-form] textarea');
        if (textarea) textarea.focus();
    }
}

/**
 * Parse one SSE frame (split on \n\n upstream) into {event, data}.
 * Multi-line data lines are rejoined with U+000A so server-rendered
 * HTML with internal newlines survives intact.
 */
function parseSseFrame(raw) {
    const lines = raw.split('\n');
    let event = 'message';
    const dataParts = [];
    for (const line of lines) {
        if (line.startsWith('event:')) {
            event = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
            dataParts.push(line.slice(5).replace(/^ /, ''));
        }
    }
    return { event, data: dataParts.join('\n') };
}

/**
 * Drop one SSE frame's pre-rendered HTML into the chat messages
 * container.
 *
 *   user_message / streaming_bubble / error   →  append at the end
 *   chunk / done                              →  replace the existing
 *                                                streaming bubble via
 *                                                outerHTML (server's
 *                                                HTML carries the
 *                                                matching id on chunk)
 */
function applyFrame(messages, frame) {
    if (!frame.data) return;
    if (frame.event === 'chunk' || frame.event === 'done') {
        const existing = messages.querySelector('#current-agent-bubble');
        if (existing) {
            existing.outerHTML = frame.data;
            return;
        }
    } else if (frame.event === 'error' || frame.event === 'thread_expired') {
        // The streaming-bubble placeholder (typing dots) stays in the
        // DOM until a chunk replaces it via outerHTML. If the turn
        // errors before any chunk arrives, the dots sit there forever
        // and look broken — strip the placeholder first, then drop in
        // the error notice underneath.
        const existing = messages.querySelector('#current-agent-bubble');
        if (existing) existing.remove();
        // The upstream thread is dead — drop the persisted conversation
        // and mark the container so saveSnapshot never re-persists the
        // dead state. The user starts over by reopening the panel.
        if (frame.event === 'thread_expired') {
            clearSnapshot();
            messages.setAttribute('data-chat-thread-expired', '');
        }
    }
    messages.insertAdjacentHTML('beforeend', frame.data);
}

async function streamFetchInto(url, formData, messages) {
    const response = await fetch(url, { method: 'POST', body: formData });
    if (!response.body) throw new Error('No response body');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
            if (!part.trim()) continue;
            applyFrame(messages, parseSseFrame(part));
            scrollChatToBottom();
        }
    }
}

async function submitChatMessage(form, messageOverride) {
    if (streaming) return;
    const messages = document.querySelector('[data-chat-messages]');
    if (!messages) return;
    const url = form.dataset.chatActionUrl;
    if (!url) return;

    const formData = new FormData(form);
    if (typeof messageOverride === 'string') {
        formData.set('message', messageOverride);
    }
    if (!String(formData.get('message') || '').trim()) return;

    setStreamingDisabled(true);
    const snapshot = new FormData();
    for (const [k, v] of formData.entries()) snapshot.append(k, v);
    form.reset();
    form.querySelectorAll('input[type="hidden"]').forEach((el) => {
        if (snapshot.has(el.name)) el.value = String(snapshot.get(el.name));
    });

    try {
        await streamFetchInto(url, formData, messages);
    } catch (err) {
        messages.insertAdjacentHTML(
            'beforeend',
            '<div class="flex justify-center"><p class="text-xs text-status-danger-text py-1">' +
            "We couldn't send your message. Please try again." +
            '</p></div>',
        );
        console.error('[chat] submit failed:', err);
    } finally {
        setStreamingDisabled(false);
        scrollChatToBottom();
        saveSnapshot();
    }
}

async function submitChatImage(imageForm) {
    if (streaming) return;
    const messages = document.querySelector('[data-chat-messages]');
    if (!messages) return;
    const fileInput = imageForm.querySelector('[data-chat-image-file]');
    const captionInput = imageForm.querySelector('[data-chat-image-caption]');
    const messageForm = document.querySelector('[data-chat-message-form]');
    const textarea = messageForm
        ? messageForm.querySelector('textarea[name="message"]')
        : null;
    if (!fileInput || !fileInput.files || !fileInput.files[0]) return;
    const file = fileInput.files[0];
    const caption = textarea ? textarea.value : '';
    if (captionInput) captionInput.value = caption;

    setStreamingDisabled(true);
    // The user-side image bubble is built locally — the server doesn't
    // have the image bytes back to render an <img src>, and we don't
    // want to ship a multi-MB data: URL through the SSE stream.
    const blobUrl = URL.createObjectURL(file);
    const captionHtml = caption
        ? '<div class="px-4 py-2 text-white/90 text-sm">' +
          caption.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') +
          '</div>'
        : '';
    messages.insertAdjacentHTML(
        'beforeend',
        '<div class="flex justify-end">' +
          '<div class="max-w-[80%] bg-teal-600 text-white rounded-2xl rounded-br-md overflow-hidden">' +
          '<img src="' + blobUrl + '" alt="Uploaded image" ' +
          'class="block max-w-full max-h-72 w-auto h-auto" />' +
          captionHtml +
          '</div></div>',
    );
    scrollChatToBottom();
    if (textarea) textarea.value = '';

    try {
        await streamFetchInto(imageForm.dataset.chatActionUrl, new FormData(imageForm), messages);
    } catch (err) {
        messages.insertAdjacentHTML(
            'beforeend',
            '<div class="flex justify-center"><p class="text-xs text-status-danger-text py-1">' +
            "We couldn't process that image. Please try again." +
            '</p></div>',
        );
        console.error('[chat] image submit failed:', err);
    } finally {
        fileInput.value = '';
        setStreamingDisabled(false);
        scrollChatToBottom();
        saveSnapshot();
    }
}

function closeChatPanel() {
    if (streaming) return;
    const mount = document.getElementById('chat-panel-mount');
    if (mount) mount.innerHTML = '';
}

function startNewChat() {
    if (streaming) return;
    clearSnapshot();
    const mount = document.getElementById('chat-panel-mount');
    if (!mount) return;
    mount.innerHTML = '';
    if (window.htmx && typeof window.htmx.ajax === 'function') {
        window.htmx.ajax('GET', PANEL_URL, {
            target: '#chat-panel-mount',
            swap: 'innerHTML',
        });
    }
}

export function initChat() {
    if (initialized) return;
    initialized = true;

    activateCaptchaGates(document);

    // Restore a persisted conversation instead of fetching the captcha
    // gate: cancel the launcher's panel GET and inject the snapshot.
    // The open-thread panel has no hx- attributes (forms are fetch-
    // based, buttons are delegated handlers below), so plain innerHTML
    // injection fully revives it.
    document.body.addEventListener('htmx:beforeRequest', (event) => {
        const config = event.detail && event.detail.requestConfig;
        if (!config || config.verb !== 'get' || config.path !== PANEL_URL) return;
        const html = loadSnapshot();
        if (!html) return;
        const mount = document.getElementById('chat-panel-mount');
        if (!mount) return;
        event.preventDefault();
        mount.innerHTML = html;
        scrollChatToBottom();
        const textarea = mount.querySelector('[data-chat-message-form] textarea');
        if (textarea) textarea.focus();
    });

    document.body.addEventListener('htmx:afterSwap', (event) => {
        const target = event.detail && event.detail.target;
        if (!target) return;
        activateCaptchaGates(target);
        if (target !== document.body) activateCaptchaGates(document.body);
        scrollChatToBottom();
        // /start just swapped the open-thread panel into the mount —
        // snapshot immediately so the solved captcha survives
        // navigation even before the first message is sent.
        if (target.id === 'chat-panel-mount' && target.querySelector('[data-chat-messages]')) {
            saveSnapshot();
        }
    });

    document.body.addEventListener('submit', (event) => {
        const form = event.target;
        if (form && form.matches && form.matches('[data-chat-message-form]')) {
            event.preventDefault();
            submitChatMessage(form);
        }
    });

    document.body.addEventListener('click', (event) => {
        if (event.target.closest && event.target.closest('[data-chat-close]')) {
            event.preventDefault();
            closeChatPanel();
            return;
        }
        if (event.target.closest && event.target.closest('[data-chat-new]')) {
            event.preventDefault();
            startNewChat();
            return;
        }
        const suggestion = event.target.closest
            ? event.target.closest('[data-chat-suggestion]')
            : null;
        if (suggestion && !suggestion.disabled) {
            event.preventDefault();
            const messageForm = document.querySelector('[data-chat-message-form]');
            if (messageForm) {
                submitChatMessage(messageForm, suggestion.dataset.chatSuggestion);
            }
            return;
        }
        const uploadBtn = event.target.closest
            ? event.target.closest('[data-chat-upload-btn]')
            : null;
        if (uploadBtn && !uploadBtn.disabled) {
            const fileInput = uploadBtn.closest('#chat-panel')
                ? uploadBtn.closest('#chat-panel').querySelector('[data-chat-image-file]')
                : null;
            if (fileInput) fileInput.click();
        }
    });

    document.body.addEventListener('change', (event) => {
        const t = event.target;
        if (!t || !t.matches || !t.matches('[data-chat-image-file]')) return;
        const imageForm = t.closest('[data-chat-image-form]');
        if (imageForm) submitChatImage(imageForm);
    });
}
